/**
 * «👥 Нагрузка по исполнителям» — `#loadBlock` + `#loadTotals` (ui-blocks.md §5.6).
 *
 * Числовая сетка из шести колонок, полос в макете нет. Кликабельны ненулевые
 * «В работе», «Горит» и «Просрочено» (ТЗ §8.2 требует переход к делам
 * исполнителя); нули получают класс `zero` и остаются обычным текстом.
 * Область «на сейчас».
 */

import type { KeyboardEvent } from 'react';
import { BLOCK_GAP, HEAD_NOTE_SM, fmtDate, num1 } from './format';
import type { LoadBlock, LoadRow, OpenList } from './types';

interface Props {
    load: LoadBlock;
    /** `range.today` — дата, на которую сделан снимок открытых дел. */
    todayIso: string;
    openList: OpenList;
}

export function ExecutorLoad({ load, todayIso, openList }: Props) {
    // Сортировка макета: сначала у кого больше горящих, при равенстве — больше открытых.
    const rows = [...load.rows].sort((a, b) => (b.hot - a.hot) || (b.open - a.open));

    // ТЗ §8.2: «клик — список дел исполнителя». В макете кликались только «Горит»
    // и «Просрочено», поэтому число «В работе» просто добавлено к ним третьим.
    const openOpen = (row: LoadRow): void => {
        openList({
            title: `В работе · ${row.full_name}`,
            kpi: 'executor_open',
            params: { executor_id: row.executor_id },
        });
    };

    const openHot = (row: LoadRow): void => {
        openList({
            title: `Горит · ${row.full_name}`,
            kpi: 'executor_hot',
            params: { executor_id: row.executor_id },
        });
    };

    const openLate = (row: LoadRow): void => {
        openList({
            title: `Просрочено · ${row.full_name}`,
            kpi: 'executor_late',
            params: { executor_id: row.executor_id },
        });
    };

    const onKeyDown = (event: KeyboardEvent<HTMLSpanElement>, run: () => void): void => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        run();
    };

    return (
        <div className="stat-block" style={BLOCK_GAP}>
            <div className="block-header">
                <span>👥 Нагрузка по исполнителям</span>
                <span style={HEAD_NOTE_SM}>открытые дела на {fmtDate(todayIso)}</span>
            </div>
            <div className="block-body" id="loadBlock">
                <div className="load-row head">
                    <span className="load-name">Исполнитель</span>
                    <span className="load-num">В работе</span>
                    <span className="load-num">Горит</span>
                    <span className="load-num">Просрочено</span>
                    <span className="load-num load-ext">Продлено</span>
                    <span className="load-num load-avg">Ср. срок</span>
                </div>
                {rows.map((row) => (
                    <div className="load-row" key={row.executor_id}>
                        <span className="load-name">
                            {row.full_name}
                            <small>{row.post && row.post !== row.full_name ? row.post : ''}</small>
                        </span>
                        {row.open > 0 ? (
                            <span
                                className="load-num link"
                                data-load="open"
                                data-exec={row.executor_id}
                                role="button"
                                tabIndex={0}
                                onClick={() => openOpen(row)}
                                onKeyDown={(event) => onKeyDown(event, () => openOpen(row))}
                            >
                                {row.open}
                            </span>
                        ) : (
                            <span className="load-num zero">0</span>
                        )}
                        {row.hot > 0 ? (
                            <span
                                className="load-num hot link"
                                data-load="hot"
                                data-exec={row.executor_id}
                                role="button"
                                tabIndex={0}
                                onClick={() => openHot(row)}
                                onKeyDown={(event) => onKeyDown(event, () => openHot(row))}
                            >
                                {row.hot}
                            </span>
                        ) : (
                            <span className="load-num zero">0</span>
                        )}
                        {row.late > 0 ? (
                            <span
                                className="load-num late link"
                                data-load="late"
                                data-exec={row.executor_id}
                                role="button"
                                tabIndex={0}
                                onClick={() => openLate(row)}
                                onKeyDown={(event) => onKeyDown(event, () => openLate(row))}
                            >
                                {row.late}
                            </span>
                        ) : (
                            <span className="load-num zero">0</span>
                        )}
                        <span className={row.extended > 0 ? 'load-num load-ext' : 'load-num load-ext zero'}>
                            {row.extended}
                        </span>
                        <span className={row.closed > 0 ? 'load-num load-avg' : 'load-num load-avg zero'}>
                            {row.closed > 0 && row.avg_days !== null ? `${num1(row.avg_days)} дн` : '—'}
                        </span>
                    </div>
                ))}
            </div>
            <div className="totals-row" id="loadTotals">
                <span>Итого открытых дел</span>
                <span>
                    <b>{load.total_open}</b> = {load.total_late} просрочено + {load.total_hot} горит +{' '}
                    {load.total_norm} в норме
                </span>
            </div>
        </div>
    );
}
